import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config/api';
import { useAuth } from '../context/AuthContext';
import useSEO from '../hooks/useSEO';
import GamePrice from '../components/GamePrice';
import AddCardModal from '../components/AddCardModal';
import CreatorBundleCard from '../components/CreatorBundleCard';
import GamesBundleCard from '../components/GamesBundleCard';
import { usesBundleUsdCheckout } from '../utils/gamePrice';
import { getStripe } from '../config/stripe';

function Bundles() {
  useSEO({
    title: 'Bundles | Nightfall',
    description: 'Get multiple games at a discounted price with our game bundles.',
    url: 'https://nightfallshop.com/bundles',
  });

  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ownedGameIds, setOwnedGameIds] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [openConfirmAfterCard, setOpenConfirmAfterCard] = useState(false);

  const fetchPaymentMethods = useCallback(async () => {
    if (!token) {
      setHasPaymentMethod(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/stripe/payment-methods`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setHasPaymentMethod(Boolean(data.hasPaymentMethod));
      }
    } catch (err) {
      console.error('Error fetching payment methods:', err);
    }
  }, [token]);

  const fetchOwnedGames = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/purchases/games`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        const ownedIds = data.games.map((g) => g.gameId);
        setOwnedGameIds(ownedIds);
      }
    } catch (err) {
      console.error('Error fetching owned games:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchBundles();
    if (isAuthenticated && token) {
      fetchOwnedGames();
      fetchPaymentMethods();
    } else {
      setHasPaymentMethod(false);
    }
  }, [isAuthenticated, token, fetchOwnedGames, fetchPaymentMethods]);

  const fetchBundles = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/bundles`);
      if (response.ok) {
        const data = await response.json();
        setBundles(data);
      }
    } catch (error) {
      console.error('Error fetching bundles:', error);
    } finally {
      setLoading(false);
    }
  };

  const completeUsdBundlePurchase = async (paymentIntentId) => {
    const response = await fetch(`${API_URL}/stripe/complete-bundle-purchase`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ paymentIntentId }),
    });
    return response.json();
  };

  const handlePurchaseClick = (bundle) => {
    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }

    const ownsAllGames = bundle.games.every((game) => ownedGameIds.includes(game.id));
    if (ownsAllGames) {
      return;
    }

    setSelectedBundle(bundle);

    if (usesBundleUsdCheckout(bundle) && !hasPaymentMethod) {
      setOpenConfirmAfterCard(true);
      setShowAddCardModal(true);
      return;
    }

    setShowConfirmModal(true);
    setPurchaseError('');
  };

  const handleConfirmPurchase = async () => {
    if (!selectedBundle) return;

    if (usesBundleUsdCheckout(selectedBundle)) {
      if (!hasPaymentMethod) {
        setShowConfirmModal(false);
        setOpenConfirmAfterCard(true);
        setShowAddCardModal(true);
        return;
      }

      setPurchasing(true);
      setPurchaseError('');

      try {
        const response = await fetch(`${API_URL}/stripe/purchase-bundle`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ bundleId: selectedBundle.id }),
        });

        const data = await response.json();

        if (data.requiresAction && data.clientSecret) {
          const stripe = await getStripe(token);
          if (!stripe) {
            setPurchaseError('Payments are unavailable right now. Please try again later.');
            return;
          }

          const { error, paymentIntent } = await stripe.confirmCardPayment(data.clientSecret);

          if (error) {
            setPurchaseError(error.message || 'Payment authentication failed');
            return;
          }

          if (paymentIntent?.status === 'succeeded') {
            await completeUsdBundlePurchase(paymentIntent.id);
            await fetchOwnedGames();
            setShowConfirmModal(false);
            setShowSuccessModal(true);
            setTimeout(() => {
              setShowSuccessModal(false);
              navigate('/account');
            }, 2000);
          }
          return;
        }

        if (response.ok && data.success) {
          await fetchOwnedGames();
          setShowConfirmModal(false);
          setShowSuccessModal(true);
          setTimeout(() => {
            setShowSuccessModal(false);
            navigate('/account');
          }, 2000);
        } else if (response.status === 409) {
          await fetchOwnedGames();
          setShowConfirmModal(false);
        } else if (data.code === 'NO_PAYMENT_METHOD') {
          setShowConfirmModal(false);
          setOpenConfirmAfterCard(true);
          setShowAddCardModal(true);
        } else {
          setPurchaseError(data.error || 'Failed to purchase bundle');
        }
      } catch (error) {
        console.error('Error purchasing bundle:', error);
        setPurchaseError('Network error. Please try again.');
      } finally {
        setPurchasing(false);
      }
      return;
    }

    setPurchaseError('This bundle is not available for card checkout yet.');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-bg p-5 pt-28">
        <div className="max-w-6xl mx-auto">
          <div className="text-white text-center py-20">Loading bundles...</div>
        </div>
      </div>
    );
  }

  return (
    <main className="p-2 lg:p-5 pt-0 flex flex-col w-full max-w-full overflow-x-hidden">
      <style>{`
        .bundles-align {
          margin-top: calc(5.75rem + 0.5rem - 6rem);
        }
        @media (min-width: 768px) {
          .bundles-align {
            margin-top: calc(5.75rem + 1.25rem - 8rem);
          }
        }
      `}</style>
      <div className="bundles-align w-full mb-4 lg:mb-6">
        <h2 className="font-semibold text-3xl lg:text-4xl text-white">Bundles</h2>
      </div>

      {bundles.length === 0 ? (
        <div className="bg-dark-card border border-dark-border rounded-3xl p-16 text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-dark-bg/50 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">No bundles available</h3>
          <p className="text-white/60 mb-8 max-w-md mx-auto">Check back later for new bundle deals!</p>
        </div>
      ) : (
        <div className="w-full max-w-full mb-8 space-y-6">
          {bundles.map((bundle) =>
            bundle.bundleType === 'creator' ? (
              <CreatorBundleCard
                key={bundle.id}
                bundle={bundle}
                onPurchaseClick={handlePurchaseClick}
                isAuthenticated={isAuthenticated}
                ownedGameIds={ownedGameIds}
              />
            ) : bundle.bundleType === 'games' ? (
              <GamesBundleCard
                key={bundle.id}
                bundle={bundle}
                onPurchaseClick={handlePurchaseClick}
                isAuthenticated={isAuthenticated}
                ownedGameIds={ownedGameIds}
              />
            ) : (
              <BundleCard
                key={bundle.id}
                bundle={bundle}
                onPurchaseClick={handlePurchaseClick}
                isAuthenticated={isAuthenticated}
                ownedGameIds={ownedGameIds}
              />
            )
          )}
        </div>
      )}

      {showConfirmModal && selectedBundle && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-4">Confirm Purchase</h3>

            <div className="mb-6">
              <div className="text-white/60 text-sm mb-2">You are purchasing:</div>
              <div className="text-white font-semibold text-lg mb-4">{selectedBundle.title}</div>

              <div className="space-y-2 mb-4">
                {selectedBundle.games.map((game) => (
                  <div key={game.id} className="text-white/70 text-sm">
                    • {game.title}
                  </div>
                ))}
              </div>

              <div className="border-t border-dark-border pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-white font-semibold">Bundle Price:</div>
                  <GamePrice bundle={selectedBundle} size="lg" />
                </div>
                {usesBundleUsdCheckout(selectedBundle) && !hasPaymentMethod && (
                  <button
                    type="button"
                    onClick={() => {
                      setOpenConfirmAfterCard(true);
                      setShowAddCardModal(true);
                    }}
                    className="w-full py-2.5 px-4 rounded-xl font-semibold text-sm transition-all duration-200 bg-accent-blue/20 border border-accent-blue/40 text-accent-blue hover:bg-accent-blue/30 hover:border-accent-blue/60"
                  >
                    Add payment method
                  </button>
                )}
              </div>
            </div>

            {purchaseError && (
              <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-xl p-3">
                <p className="text-red-400 text-sm">{purchaseError}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setPurchaseError('');
                  setSelectedBundle(null);
                }}
                disabled={purchasing}
                className="flex-1 py-3 px-6 rounded-xl font-semibold text-base transition-all duration-200 bg-dark-surface/50 border border-dark-border text-white hover:bg-dark-surface/70 hover:border-white/20 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmPurchase}
                disabled={purchasing || (usesBundleUsdCheckout(selectedBundle) && !hasPaymentMethod)}
                className="flex-1 py-3 px-6 rounded-xl font-semibold text-base transition-all duration-200 bg-accent-blue text-white hover:bg-accent-blue/90 shadow-lg shadow-accent-blue/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {purchasing ? 'Processing...' : 'Confirm Purchase'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && selectedBundle && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl text-center">
            <div className="mb-6 flex justify-center">
              <div className="w-16 h-16 rounded-full bg-green-500/20 border-2 border-green-500/50 flex items-center justify-center">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-white mb-2">Purchase Successful!</h3>
            <p className="text-white/70 text-base mb-6">{selectedBundle.title} has been added to your library.</p>
            <p className="text-white/50 text-sm">Redirecting to your account...</p>
          </div>
        </div>
      )}

      <AddCardModal
        isOpen={showAddCardModal}
        onClose={() => {
          setShowAddCardModal(false);
          setOpenConfirmAfterCard(false);
        }}
        onSuccess={async () => {
          await fetchPaymentMethods();
          if (openConfirmAfterCard && selectedBundle) {
            setShowConfirmModal(true);
            setOpenConfirmAfterCard(false);
          }
        }}
        title="Add a card to purchase"
        description={
          selectedBundle
            ? `Save a card to buy ${selectedBundle.title}. It stays on your account for future purchases.`
            : 'Save a card to your account for quick checkout.'
        }
      />
    </main>
  );
}

function BundleCard({ bundle, onPurchaseClick, isAuthenticated, ownedGameIds }) {
  const navigate = useNavigate();
  const ownsAllGames = bundle.games.every((game) => ownedGameIds.includes(game.id));
  const isOwned = ownsAllGames;

  return (
    <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl hover:shadow-accent-blue/20 transition-all duration-300">
      <div className="p-6">
        <h3 className="text-xl font-semibold text-white mb-4">{bundle.title}</h3>

        <div className="mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bundle.games.map((game) => (
              <div
                key={game.id}
                onClick={() => navigate(`/game/${game.id}`)}
                className="flex items-center gap-5 p-5 bg-dark-bg/30 border border-dark-border rounded-xl hover:bg-dark-bg/50 hover:border-accent-blue/30 transition-all cursor-pointer group"
              >
                {game.mainImage && (
                  <div className="w-40 aspect-[16/9] flex-shrink-0 rounded-lg overflow-hidden border border-dark-border">
                    <img
                      src={game.mainImage}
                      alt={game.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold text-lg mb-1.5 group-hover:text-accent-blue transition-colors truncate">
                    {game.title}
                  </div>
                  {game.developer && (
                    <div className="text-white/50 text-base mb-1.5 truncate">{game.developer}</div>
                  )}
                  <GamePrice game={game} size="sm" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-dark-border pt-4">
          <div className="flex items-center justify-between">
            <GamePrice bundle={bundle} size="lg" />
            <button
              onClick={() => onPurchaseClick(bundle)}
              disabled={!isAuthenticated || isOwned}
              className="py-3 px-6 bg-accent-blue text-white font-semibold rounded-xl hover:bg-accent-blue/90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {!isAuthenticated ? 'Sign In to Purchase' : isOwned ? 'Owned' : 'Purchase'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Bundles;
